import React, { useState } from 'react';
import { Badge, Popover, List, Typography, Button, Empty } from 'antd';
import { BellOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNotifications } from '../contexts/NotificationContext';
import { relativeTime } from '../utils/relativeTime';
import './NotificationCenter.css';

const { Text } = Typography;

const NotificationCenter = () => {
  const { notifications, removeNotification, clearAll, getUnreadCount } = useNotifications();
  const [visible, setVisible] = useState(false);
  const unreadCount = getUnreadCount();

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined className="notification-center-icon notification-center-icon-success" />;
      case 'error':
        return <CloseCircleOutlined className="notification-center-icon notification-center-icon-error" />;
      case 'processing':
        return <LoadingOutlined className="notification-center-icon notification-center-icon-processing" spin />;
      default:
        return <LoadingOutlined className="notification-center-icon notification-center-icon-pending" spin />;
    }
  };

  const formatTime = (timestamp) => relativeTime(timestamp instanceof Date ? timestamp.toISOString() : timestamp);

  // Sort notifications by timestamp (newest first)
  const sortedNotifications = [...notifications].sort((a, b) => {
    const timeA = new Date(a.timestamp).getTime();
    const timeB = new Date(b.timestamp).getTime();
    return timeB - timeA; // Descending order (newest first)
  });

  const content = (
    <div className="notification-center-popover">
      <div className="notification-center-header">
        <Text strong>Notifications</Text>
        {notifications.length > 0 && (
          <Button
            type="text"
            size="small"
            onClick={clearAll}
            className="notification-center-clear"
          >
            Clear all
          </Button>
        )}
      </div>
      <div className="notification-center-list">
        {sortedNotifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notifications"
            style={{ padding: '20px 0' }}
          />
        ) : (
          <List
            dataSource={sortedNotifications}
            renderItem={(notification) => (
              <List.Item
                className={`notification-center-item notification-center-item-${notification.status}`}
                actions={[
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeNotification(notification.id)}
                    className="notification-center-delete"
                  />,
                ]}
              >
                <List.Item.Meta
                  avatar={getStatusIcon(notification.status)}
                  title={
                    <div className="notification-center-item-title">
                      {notification.title}
                    </div>
                  }
                  description={
                    <div>
                      {notification.message && (
                        <div className="notification-center-item-message">
                          {notification.message}
                        </div>
                      )}
                      {notification.progress !== null && notification.progress !== undefined && (
                        <div className="notification-center-item-progress">
                          Progress: {notification.progress}%
                        </div>
                      )}
                      <div className="notification-center-item-time">
                        {formatTime(notification.timestamp)}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      title={null}
      trigger="click"
      placement="bottomRight"
      visible={visible}
      onVisibleChange={setVisible}
      overlayClassName="notification-center-overlay"
    >
      <div className="notification-center-trigger">
        <Badge count={unreadCount} offset={[-2, 2]} size="small">
          <BellOutlined className="notification-center-bell" />
        </Badge>
      </div>
    </Popover>
  );
};

export default NotificationCenter;
